"use client"

import type React from "react"
import { useState } from "react"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { CardPreview } from "@/components/preview/card-preview"
import { TablePreview } from "@/components/preview/table-preview"
import { ListPreview } from "@/components/preview/list-preview"
import { CardArrayPreview } from "@/components/preview/card-array-preview"
import { GripVertical, X } from "lucide-react"
import { useVisualizer } from "@/lib/visualizer-context"
import { Button } from "@/components/ui/button"

interface FormPreviewProps {
	config: any
	dataSchema: any
	data?: any
	editable?: boolean
	onDataChange?: (newData: any) => void
}

export function FormPreview({ config, dataSchema, data, editable = false, onDataChange }: FormPreviewProps) {
	document.documentElement.classList.remove("disable-scroll")
	const isMobile = typeof window !== "undefined" && window.innerWidth <= 1024
	const { setVisualizerConfig, reorderKeys, reorderField, removeField, deleteTab } = useVisualizer()
	const [draggedIndex, setDraggedIndex] = useState<number | null>(null)
	const [dragOverIndex, setDragOverIndex] = useState<number | null>(null)
	const [currentTabIndex, setCurrentTabIndex] = useState(0)
	console.log("FormPreview config:", config)

	const handleFieldChange = (path: string[], value: any) => {
		if (!onDataChange || !data) return

		const newData = JSON.parse(JSON.stringify(data))
		let current = newData

		for (let i = 0; i < path.length - 1; i++) {
			if (current[path[i]] === undefined) {
				current[path[i]] = {}
			}
			current = current[path[i]]
		}

		current[path[path.length - 1]] = value
		onDataChange(newData)
	}

	const handleDragStart = (e: React.DragEvent, index: number) => {
		e.dataTransfer.effectAllowed = "move"
		setDraggedIndex(index)
	}

	const handleDragEnd = () => {
		setDraggedIndex(null)
		setDragOverIndex(null)
	}

	const handleDragOver = (e: React.DragEvent, index: number) => {
		e.preventDefault()
		e.dataTransfer.dropEffect = "move"
		if (draggedIndex !== null && draggedIndex !== index) {
			setDragOverIndex(index)
		}
	}

	const handleDrop = (e: React.DragEvent, dropIndex: number) => {
		e.preventDefault()

		if (draggedIndex === null || draggedIndex === dropIndex) {
			setDragOverIndex(null)
			return
		}

		const newConfig = JSON.parse(JSON.stringify(config))
		const currentTab = newConfig.tabs[currentTabIndex]
		const layout = currentTab.layout

		const [movedItem] = layout.splice(draggedIndex, 1)
		const adjustedDropIndex = dropIndex > draggedIndex ? dropIndex - 1 : dropIndex
		layout.splice(adjustedDropIndex, 0, movedItem)

		setVisualizerConfig(newConfig)
		setDraggedIndex(null)
		setDragOverIndex(null)
	}

	const handleDeleteTab = (tabIndex: number, e: React.MouseEvent) => {
		e.stopPropagation()

		if (config.tabs.length <= 1) {
			return
		}

		deleteTab(tabIndex)

		if (currentTabIndex >= config.tabs.length - 1) {
			setCurrentTabIndex(Math.max(0, config.tabs.length - 2))
		}
	}

	const renderItem = (
		item: any,
		parentData?: any,
		parentScope?: string,
		componentPath: number[] = [],
		parentPath: string[] = [],
	): React.ReactNode => {
		const stableKey = `tab-${currentTabIndex}-${componentPath.join("-")}` || `tab-${currentTabIndex}-0`

		switch (item.type) {
			case "card":
				return (
					<CardPreview
						key={stableKey}
						item={item}
						dataSchema={dataSchema}
						data={data}
						parentData={parentData}
						parentPath={parentPath}
						editable={editable}
						draggable={!editable}
						onFieldChange={editable ? handleFieldChange : undefined}
						renderItem={(child, childParentData, childParentScope, childParentPath) =>
							renderItem(child, childParentData, childParentScope, [...componentPath], childParentPath || parentPath)
						}
						tabIndex={currentTabIndex}
						componentPath={componentPath}
						onReorderKeys={!editable ? reorderKeys : undefined}
						onReorderField={!editable ? reorderField : undefined}
						onRemoveField={!editable ? removeField : undefined}
					/>
				)

			case "card_array":
				return (
					<CardArrayPreview
						key={stableKey}
						item={item}
						dataSchema={dataSchema}
						data={data}
						parentData={parentData}
						parentPath={parentPath}
						editable={editable}
						draggable={!editable}
						onFieldChange={editable ? handleFieldChange : undefined}
						renderItem={(child, childParentData, childParentScope, childParentPath) =>
							renderItem(child, childParentData, childParentScope, [...componentPath], childParentPath || parentPath)
						}
						tabIndex={currentTabIndex}
						componentPath={componentPath}
						onReorderKeys={!editable ? reorderKeys : undefined}
						onReorderField={!editable ? reorderField : undefined}
						onRemoveField={!editable ? removeField : undefined}
					/>
				)

			case "table":
				return (
					<TablePreview
						key={stableKey}
						item={item}
						dataSchema={dataSchema}
						data={data}
						parentData={parentData}
						parentScope={parentScope}
						editable={editable}
						parentPath={parentPath}
						onFieldChange={editable ? handleFieldChange : undefined}
						draggable={!editable}
						tabIndex={currentTabIndex}
						componentPath={componentPath}
						onReorderKeys={!editable ? reorderKeys : undefined}
					/>
				)

			case "list":
				return (
					<ListPreview
						key={stableKey}
						item={item}
						data={data}
						parentData={parentData}
						schema={dataSchema}
						editable={editable}
						onDataChange={
							editable
								? (path, value) => {
									const pathParts = path.split(/[.[\]]/).filter((p) => p)
									handleFieldChange(pathParts, value)
								}
								: undefined
						}
					/>
				)

			default:
				return (
					<div key={stableKey} className="p-4 border border-dashed border-muted-foreground/50 rounded-lg">
						<p className="text-sm text-muted-foreground">Unknown component type: {item.type}</p>
					</div>
				)
		}
	}

	if (!config || !config.tabs || config.tabs.length === 0) {
		return (
			<div className="flex items-center justify-center h-full">
				<div className="text-center space-y-2">
					<p className="text-lg font-medium">No configuration found</p>
					<p className="text-sm text-muted-foreground">Add components to the canvas to see the preview</p>
				</div>
			</div>
		)
	}

	return (
		<div className="h-full overflow-auto md:p-6 bg-muted/30">
			<div className="max-w-5xl mx-auto">

				<Tabs
					value={currentTabIndex.toString()}
					onValueChange={(v) => setCurrentTabIndex(Number.parseInt(v))}
					className="w-full"
				>
					{/* DESKTOP → render tabs */}
					{!isMobile && (
						<TabsList className="mb-6">
							{config.tabs.map((tab: any, index: number) => (
								<TabsTrigger key={index} value={index.toString()} className="group relative">
									{tab.title || `Tab ${index + 1}`}
									{!editable && config.tabs.length > 1 && (
										<Button
											variant="ghost"
											size="icon"
											className="h-4 w-4 ml-2 opacity-0 group-hover:opacity-100 transition-opacity"
											onClick={(e) => handleDeleteTab(index, e)}
										>
											<X className="h-3 w-3" />
										</Button>
									)}
								</TabsTrigger>
							))}
						</TabsList>
					)}

					{/* MOBILE → dropdown instead of tabs */}
					{isMobile && (
						<div className="mb-4">
							<Select
								value={currentTabIndex.toString()}
								onValueChange={(v) => setCurrentTabIndex(Number.parseInt(v))}
							>
								<SelectTrigger>
									<SelectValue placeholder="Select section…" />
								</SelectTrigger>
								<SelectContent>
									{config.tabs.map((tab: any, index: number) => (
										<SelectItem key={index} value={index.toString()}>
											{tab.title || `Tab ${index + 1}`}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
					)}

					{/* CONTENT */}
					{config.tabs.map((tab: any, tabIndex: number) => (
						<TabsContent key={tabIndex} value={tabIndex.toString()} className="space-y-6">
							{tab.layout && tab.layout.length > 0 ? (
								tab.layout.map((item: any, index: number) => (
									<div
										key={index}
										draggable={!editable}
										onDragStart={!editable ? (e) => handleDragStart(e, index) : undefined}
										onDragEnd={!editable ? handleDragEnd : undefined}
										onDragOver={!editable ? (e) => handleDragOver(e, index) : undefined}
										onDrop={!editable ? (e) => handleDrop(e, index) : undefined}
										className={`${!editable ? "group" : ""} relative transition-all ${dragOverIndex === index ? "scale-105" : ""}`}
									>
										{!editable && (
											<div className="absolute -left-8 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing">
												<GripVertical className="h-5 w-5 text-muted-foreground" />
											</div>
										)}

										{!editable && dragOverIndex === index && draggedIndex !== null && draggedIndex !== index && (
											<div className="absolute -top-3 left-0 right-0 h-1 bg-primary rounded-full" />
										)}

										{renderItem(item, undefined, undefined, [index], [])}
									</div>
								))
							) : (
								<div className="text-center py-12">
									<p className="text-muted-foreground">No components in this tab</p>
								</div>
							)}
						</TabsContent>
					))}
				</Tabs>
			</div>
		</div>
	)
}

